/**
 * Stable error codes returned by the auth API. Backend throws Nest
 * exceptions with `{ code, message }` bodies; the web surfaces `code`
 * via `ApiError.code` and looks up the localized copy by code instead
 * of relying on the English `message` field.
 *
 * The values double as i18n keys on the web side (`auth.errors.*`):
 * the constant is the runtime payload, the key happens to be the
 * same string. That coupling is deliberate — it removes a translation
 * map from the client and means a code added here automatically
 * needs a corresponding RU/RO entry, which the missing-key warning
 * in `useT()` will surface in dev.
 *
 * If you add a code: also add it to:
 *   - `apps/web/src/lib/i18n/strings.ru.ts`
 *   - `apps/web/src/lib/i18n/strings.ro.ts`
 */
export const AUTH_ERROR_CODES = {
  /** Login: email not found OR bcrypt mismatch. Indistinguishable on purpose. */
  INVALID_CREDENTIALS: "auth.errors.invalid_credentials",
  /** Register: a user already exists with this email. */
  EMAIL_ALREADY_REGISTERED: "auth.errors.email_already_registered",
  /** /password/reset: token bad/expired/used. We don't tell which (anti-enumeration). */
  RESET_LINK_INVALID: "auth.errors.reset_link_invalid",
  /** /email/verify/confirm: token bad/expired/used. Same generic-by-design copy. */
  VERIFY_LINK_INVALID: "auth.errors.verify_link_invalid",
  /** 429 — request was rate-limited. Body also carries `retryAfterSec`. */
  RATE_LIMITED: "auth.errors.rate_limited",
  /** Generic 401 — JWT missing or invalid. Surfaced for completeness. */
  UNAUTHORIZED: "auth.errors.unauthorized",
} as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];

/**
 * Stable validation message keys used as Zod `.message` strings on
 * the schemas in `./schemas.ts`. The web's `FieldError` component
 * detects strings starting with `"validation."` and translates them
 * via `useT()` rather than rendering verbatim — same coupling
 * pattern as `AUTH_ERROR_CODES`, where the constant value is also
 * the i18n key.
 *
 * Server-side these strings flow through the `ZodValidationPipe`
 * unchanged. Server-side messages aren't user-visible (clients
 * validate first, and our internal callers don't read the body),
 * so they staying as keys is fine.
 */
export const VALIDATION_KEYS = {
  REQUIRED: "validation.required",
  EMAIL_INVALID: "validation.email_invalid",
  PASSWORD_TOO_SHORT: "validation.password_too_short",
  PASSWORD_TOO_LONG: "validation.password_too_long",
  PASSWORDS_MUST_MATCH: "validation.passwords_must_match",
  TOKEN_INVALID: "validation.token_invalid",
  NAME_TOO_LONG: "validation.name_too_long",
} as const;
